/**
 * Turns whatever went wrong into words a person can act on.
 *
 * The server replies with a message and, for form problems, a list of fields with developer-style text
 * ("This field may not be blank."). Nothing from here should reach the screen as it is: it goes through
 * parseError, which also works out which field each message belongs to.
 */
import i18n from 'i18next' // the app's shared instance, set up in i18n/index.ts

type T = (key: string, options?: Record<string, unknown>) => string
const defaultT: T = (key, options) => (i18n.t(key, { ns: 'common', ...options }) as string) ?? key

export type ErrorKind = 'network' | 'timeout' | 'session' | 'permission' | 'notFound' | 'conflict' | 'validation' | 'tooMany' | 'tooLarge' | 'server' | 'unknown'

export interface FriendlyError {
  kind: ErrorKind
  /** One sentence for a toast or a banner. */
  message: string
  /** Messages by field name (nested names joined with dots), for showing under inputs. */
  fields: Record<string, string>
  status?: number
  /** True when trying again later may work. */
  retryable: boolean
}

// Technical sentences the backend (or a library) puts in `message`. Never shown as they are.
const TECHNICAL = /^(validation (failed|error)|an error occurred|permission denied|resource not found|authentication required|bad request|not found|forbidden|unauthorized|internal server error|request failed with status code \d+|network error)\.?$/i

const WORDS: Record<string, string> = { no: 'number', num: 'number', dob: 'date of birth', id: 'ID', ne: '(Nepali)', en: '(English)', pan: 'PAN', vat: 'VAT', gps: 'GPS', url: 'web address' }

/** registration_no -> "Registration number", full_name_ne -> "Full name (Nepali)". */
export function humanizeField(name: string): string {
  const parts = name.split('.').pop()!.split(/[_\s]+/).filter(Boolean)
  if (!parts.length) return name
  const words = parts.map((w) => WORDS[w.toLowerCase()] ?? w.toLowerCase())
  const text = words.join(' ').replace(/\s+\(/g, ' (')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Rewrites one server message for one field. Unknown messages pass through unless they sound technical. */
export function friendlyFieldMessage(raw: string, fieldName: string, t: T = defaultT): string {
  const field = humanizeField(fieldName)
  const m = (re: RegExp) => re.exec(raw)
  let x: RegExpExecArray | null
  if (/valid (integer|number)/i.test(raw) === false && /(may not be (blank|null)|is required|required\.?$|cannot be (blank|empty)|must (not )?be empty)/i.test(raw)) return t('friendly.field.required', { field: field.toLowerCase() })
  if ((x = m(/no more than (\d+) characters/i)) || (x = m(/at most (\d+) characters/i))) return t('friendly.field.tooLong', { field, max: x[1] })
  if ((x = m(/at least (\d+) characters/i))) return t('friendly.field.tooShort', { field, min: x[1] })
  if ((x = m(/greater than or equal to (-?\d+(?:\.\d+)?)/i))) return t('friendly.field.atLeast', { field, min: x[1] })
  if ((x = m(/less than or equal to (-?\d+(?:\.\d+)?)/i))) return t('friendly.field.atMost', { field, max: x[1] })
  if (/valid email/i.test(raw)) return t('friendly.field.email')
  if (/valid integer|whole number/i.test(raw)) return t('friendly.field.wholeNumber', { field })
  if (/valid number|a number is required|decimal/i.test(raw)) return t('friendly.field.number', { field })
  if (/already exists|must be unique|already in use|already taken/i.test(raw)) return t('friendly.field.taken', { field })
  if (/invalid (pk|choice)|is not a valid choice|object does not exist|not a valid option/i.test(raw)) return t('friendly.field.choose', { field: field.toLowerCase() })
  if (/date has wrong format|valid date|datetime has wrong format/i.test(raw)) return t('friendly.field.date', { field: field.toLowerCase() })
  if (/time has wrong format|valid time/i.test(raw)) return t('friendly.field.time', { field: field.toLowerCase() })
  if (/valid url/i.test(raw)) return t('friendly.field.url')
  if (/no file was submitted|the submitted data was not a file/i.test(raw)) return t('friendly.field.file', { field: field.toLowerCase() })
  if (/only digits|digits only/i.test(raw)) return t('friendly.field.digits', { field })
  if (/too common|entirely numeric|too similar|too short.*password|password.*too/i.test(raw)) return t('friendly.field.password')
  if (/^(invalid|bad|malformed)\b/i.test(raw) || TECHNICAL.test(raw)) return t('friendly.field.invalid', { field: field.toLowerCase() })
  return raw.trim()
}

type Flat = { fields: Record<string, string>; general: string[] }

function flatten(value: unknown, path: string, out: Flat, t: T) {
  if (value == null) return
  if (typeof value === 'string') {
    if (!path || path === 'detail' || path === 'non_field_errors') out.general.push(TECHNICAL.test(value) ? '' : value)
    else if (!out.fields[path]) out.fields[path] = friendlyFieldMessage(value, path, t)
    return
  }
  if (Array.isArray(value)) {
    // A list of strings is that field's messages; a list of objects is one entry per row (a formset), keyed by position.
    value.forEach((v, i) => (typeof v === 'string' ? flatten(v, path, out, t) : flatten(v, path ? `${path}.${i}` : String(i), out, t)))
    return
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) flatten(v, path && !['detail', 'non_field_errors'].includes(k) ? `${path}.${k}` : k, out, t)
  }
}

const NETWORK_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNREFUSED'])

export function parseError(err: unknown, t: T = defaultT): FriendlyError {
  const e = err as { response?: { status?: number; data?: any }; code?: string; message?: string } | undefined
  const status = e?.response?.status
  const data = e?.response?.data

  if (!e?.response) {
    const timeout = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message ?? '')
    if (timeout) return { kind: 'timeout', message: t('friendly.timeout'), fields: {}, retryable: true }
    if (e?.code && NETWORK_CODES.has(e.code) || /network error/i.test(e?.message ?? '')) return { kind: 'network', message: t('friendly.network'), fields: {}, retryable: true }
    // A plain Error thrown by our own code: its text was written for people, keep it unless it sounds technical.
    const own = e?.message
    return { kind: 'unknown', message: own && !TECHNICAL.test(own) ? own : t('friendly.unknown'), fields: {}, retryable: true }
  }

  const flat: Flat = { fields: {}, general: [] }
  if (data && typeof data === 'object') {
    // Our envelope puts field messages in `errors`; a plain DRF view puts them at the top level.
    const ENVELOPE = ['success', 'data', 'message', 'meta', 'errors', 'detail']
    const bare = Object.fromEntries(Object.entries(data).filter(([k]) => !ENVELOPE.includes(k)))
    flatten(data.errors ?? (data.detail ? { detail: data.detail } : Object.keys(bare).length ? bare : undefined), '', flat, t)
  }
  const general = flat.general.filter(Boolean)
  const serverMessage: string | undefined = typeof data?.message === 'string' && !TECHNICAL.test(data.message) ? data.message : undefined

  const base = (kind: ErrorKind, key: string, retryable = false): FriendlyError => ({
    kind, message: general[0] ?? serverMessage ?? t(key), fields: flat.fields, status, retryable,
  })
  if (status === 400 || status === 422) {
    const count = Object.keys(flat.fields).length
    // A field's own reason beats the server's generic "check the highlighted fields" line, which says nothing a person can act on.
    const message = general[0] ?? (count === 1 ? Object.values(flat.fields)[0] : count > 1 ? t('friendly.checkFields', { count }) : serverMessage ?? t('friendly.checkDetails'))
    return { kind: 'validation', message, fields: flat.fields, status, retryable: false }
  }
  if (status === 401) return base('session', 'friendly.session')
  if (status === 403) return base('permission', 'friendly.forbidden')
  if (status === 404) return base('notFound', 'friendly.notFound')
  if (status === 408 || status === 504) return base('timeout', 'friendly.timeout', true)
  if (status === 409) return base('conflict', 'friendly.conflict')
  if (status === 413) return base('tooLarge', 'friendly.tooLarge')
  if (status === 415) return base('unknown', 'friendly.unsupported')
  if (status === 429) return base('tooMany', 'friendly.tooMany', true)
  if (status && status >= 500) return { kind: 'server', message: t('friendly.server'), fields: flat.fields, status, retryable: true }
  return base('unknown', 'friendly.unknown')
}

/** One sentence for a toast. Use as: onError: (err) => toast.error(errorMessage(err)). */
export const errorMessage = (err: unknown, fallback?: string, t: T = defaultT): string => {
  const f = parseError(err, t)
  return f.kind === 'unknown' && fallback ? fallback : f.message
}

/**
 * Puts the server's field messages under the matching inputs of a react-hook-form form.
 * `known` lists the fields the form really has; messages for other fields are returned so the form can show them
 * in its banner instead of losing them. Returns { shown, leftover }.
 */
export function applyServerErrors(
  err: unknown,
  setError: (name: any, error: { type: string; message: string }) => void,
  known?: string[],
  t: T = defaultT
): { shown: string[]; leftover: string[]; error: FriendlyError } {
  const error = parseError(err, t)
  const shown: string[] = []
  const leftover: string[] = []
  for (const [name, message] of Object.entries(error.fields)) {
    const top = name.split('.')[0]
    if (!known || known.includes(name) || known.includes(top)) {
      setError(known?.includes(name) ? name : known?.includes(top) ? top : name, { type: 'server', message })
      shown.push(name)
    } else {
      leftover.push(`${humanizeField(name)}: ${message}`)
    }
  }
  return { shown, leftover, error }
}

/** Like errorMessage, but a form problem lists what is wrong with each field. For toasts on forms with no banner. */
export const errorDetail = (err: unknown, fallback?: string, t: T = defaultT): string => {
  const f = parseError(err, t)
  const msgs = Object.values(f.fields)
  if (f.kind === 'validation' && msgs.length) return msgs.join(' ')
  return f.kind === 'unknown' && fallback ? fallback : f.message
}
